import {createStore, applyMiddleware, combineReducers} from 'redux';
import { persistReducer, persistStore, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { createReduxHistoryContext } from "redux-first-history";
import { reducer as toastr } from 'react-redux-toastr';
import thunk from 'redux-thunk';
import {createBrowserHistory} from 'history';

import { productsReducer } from './features/products';
import { servicersReducer } from './features/servicers';
import { categoriesReducer } from './features/categories';
import { cartReducer } from './features/cart';
import { addressReducer } from './features/address';
import { ordersReducer } from './features/orders';
import { favoritesReducer } from './features/favorites';
import { reviewsReducer } from './features/reviews';
import { shippingReducer } from './features/shipping';
import { promoReducer } from "./features/promo";
import { bonusesReducer } from "./features/bonuses";
import { geoReducer } from './features/geolocation';
import { profileReducer } from './features/profile';
import { bannersReducer } from "./features/banners";
import { widgetsReducer } from "./features/widgets";
import { catalogueReducer } from "./features/catalogue";
import { secondaryMenuReducer } from "./features/secondaryMenu";
import { authReducer } from "./features/auth";
import { uiReducer } from "./features/ui";
import { languageReducer } from "./features/language";
import { searchReducer } from "./features/search";
import { companiesReducer } from "./features/companies";
import { variationsReducer } from "./features/variations";
import { faqReducer } from "./features/faq";


export const mergeObjectArrays = (fromArray, toArray) => {

    let newArray = structuredClone(toArray);
    fromArray.forEach((element) => {
        const index = newArray.findIndex(x => x.id === element.id);
        if (index !== -1)
            newArray[index] = element;
        else
            newArray.push(element);
    });

    return newArray;
}

const { createReduxHistory, routerMiddleware, routerReducer } =
    createReduxHistoryContext({
        history: createBrowserHistory(),
        savePreviousLocations: 2,
});


const sliceFilter = createTransform(
    (inboundState, key) => {
        const filters = {
            auth: ['loading', 'error', 'otpSession', 'otpPhone'],
            orders: ['loading', 'error', 'items', 'byId', 'allIds'],
            shipping: ['pickupPoints', 'pickupPointsLoading', 'pickupPointsError'],
            profile: ['isSaving', 'isLoading', 'isChangingPassword', 'saveError', 'loadError',
                'passwordError', 'saveSuccess', 'passwordChangeSuccess',
                'ordersLoading', 'ordersError', 'servicesLoading', 'servicesError',
                'documentsLoading', 'documentsError'],
            variations: ['isFetching'],
            bonuses: ['loading', 'applying', 'error'],
            search: ['isFetching', 'hasMore', 'items', 'fromContext'],
            companies: ['isFetching', 'hasMore'],
            faq: ['isFetching', 'hasMore'],
        };

        const keysToRemove = filters[key];
        if (!keysToRemove) return inboundState;

        const filtered = { ...inboundState };
        keysToRemove.forEach(k => delete filtered[k]);
        return filtered;
    },
    (outboundState) => outboundState,
);


(function cleanupOldPersist() {
    try {
        const oldKeys = ['auth', 'cart', 'favorites', 'address', 'orders', 'shipping',
            'promo', 'geolocation', 'profile', 'catalogue', 'language', 'variations',
            'search', 'companies', 'faq', 'products', 'servicers', 'categories',
            'banners', 'widgets', 'reviews'];

        const hasOldKeys = oldKeys.some(k => localStorage.getItem(`persist:${k}`) !== null);
        if (hasOldKeys) {
            oldKeys.forEach(k => localStorage.removeItem(`persist:${k}`));
            localStorage.removeItem('persist:root');
        }

        // Fix: profileReducer REHYDRATE bug spread entire root state into profile slice.
        // Clean corrupted profile data that contains copies of other slices.
        const rootData = localStorage.getItem('persist:root');
        if (rootData) {
            const parsed = JSON.parse(rootData);
            if (parsed.profile) {
                const profile = JSON.parse(parsed.profile);
                if (profile.auth || profile.cart || profile.favorites || profile._persist) {
                    const validKeys = new Set([
                        'activeSection', 'fields', 'originalFields',
                        'orders', 'ordersPagination',
                        'services', 'servicesPagination',
                        'documents',
                    ]);
                    const cleaned = {};
                    for (const key of Object.keys(profile)) {
                        if (validKeys.has(key)) {
                            cleaned[key] = profile[key];
                        }
                    }
                    parsed.profile = JSON.stringify(cleaned);
                    localStorage.setItem('persist:root', JSON.stringify(parsed));
                }
            }
        }
    } catch (e) { /* */ }
})();

const rootPersistConfig = {
    key: 'root',
    storage,
    blacklist: [
        'ui',
        'toastr',
        'router',
        'products',
        'servicers',
        'categories',
        'banners',
        'widgets',
        'reviews',
        'secondaryMenu',
    ],
    transforms: [sliceFilter],
};

const combinedReducer = combineReducers({
    products: productsReducer,
    servicers: servicersReducer,
    categories: categoriesReducer,
    cart: cartReducer,
    address: addressReducer,
    orders: ordersReducer,
    favorites: favoritesReducer,
    reviews: reviewsReducer,
    shipping: shippingReducer,
    promo: promoReducer,
    bonuses: bonusesReducer,
    banners: bannersReducer,
    geolocation: geoReducer,
    profile: profileReducer,
    widgets: widgetsReducer,
    catalogue: catalogueReducer,
    secondaryMenu: secondaryMenuReducer,
    auth: authReducer,
    ui: uiReducer,
    variations: variationsReducer,
    search: searchReducer,
    companies: companiesReducer,
    language: languageReducer,
    faq: faqReducer,
    toastr,
    router: routerReducer,
});

const rootReducer = persistReducer(rootPersistConfig, combinedReducer);

export const store = createStore(
    rootReducer,
    undefined,
    applyMiddleware(thunk, routerMiddleware)
);

export const routerHistory = createReduxHistory(store);
export const persistor = persistStore(store);
